import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Globe } from "lucide-react";
import { LogoSimple, Cloud } from "./decor";
import { useGoTo } from "../nav";
import { useLang } from "../i18n";

const LINKS_L = [
  { key: "home", tid: "home", href: "/" },
  { key: "about", tid: "about-us", href: "#about" },
  { key: "programs", tid: "our-programs", href: "#programs" },
];

const LINKS_R = [
  { key: "gallery", tid: "gallery", href: "/gallery" },
  { key: "contact", tid: "contact-us", href: "/contact" },
];

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <button
      data-testid="lang-toggle"
      onClick={() => setLang(lang === "en" ? "es" : "en")}
      aria-label={lang === "en" ? "Switch to Spanish" : "Cambiar a inglés"}
      className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F9C6E3] via-[#E4D3F0] to-[#CDEBFB] text-forest font-display font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-2 shadow-card hover:shadow-soft hover:scale-[1.03] transition ${className}`}
    >
      <Globe className="w-4 h-4 text-forest/70 shrink-0" />
      <span className="hidden sm:inline">{lang === "en" ? "Haga clic para Español" : "Click for English"}</span>
      <span className="sm:hidden">{lang === "en" ? "Español" : "English"}</span>
    </button>
  );
};

const NavLink = ({ link }) => {
  const { s } = useLang();
  const goTo = useGoTo();
  return (
    <a
      data-testid={`nav-link-${link.tid}`}
      href={link.href}
      onClick={(e) => {
        e.preventDefault();
        goTo(link.href);
      }}
      className="font-display font-medium text-forest text-[15px] whitespace-nowrap hover:text-darksage transition-colors"
    >
      {s.nav[link.key]}
    </a>
  );
};

export const EnrollButton = ({ className = "", onClick }) => {
  const { s } = useLang();
  const goTo = useGoTo();
  return (
    <a
      data-testid="enroll-now-button"
      href="/contact"
      onClick={(e) => {
        e.preventDefault();
        goTo("/contact");
        if (onClick) onClick();
      }}
      className={`inline-flex items-center justify-center rounded-full bg-darksage text-white font-display font-medium px-5 py-2.5 text-[15px] hover:bg-[#4a338f] hover:scale-[1.03] transition-all duration-200 ${className}`}
    >
      {s.nav.enroll}
    </a>
  );
};

export default function Header() {
  const { s } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const goTo = useGoTo();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <Cloud className="hidden lg:block absolute left-[6%] -top-1 w-16 opacity-80 animate-drift" />
      <Cloud className="hidden lg:block absolute right-[5%] -top-2 w-20 opacity-70 animate-floaty" />

      <div className="mx-auto max-w-7xl px-3 sm:px-6 pt-3">
        <div className="relative" style={{ filter: "drop-shadow(0 12px 24px rgba(53,75,62,0.16))" }}>
          <div
            data-testid="site-header"
            className={`relative flex items-center justify-between h-16 rounded-full bg-sage px-4 sm:px-8 transition-shadow ${scrolled ? "shadow-header" : ""}`}
          >
            {/* left nav (desktop) */}
            <nav className="hidden xl:flex items-center gap-7 flex-[0.75]">
              {LINKS_L.map((l) => (
                <NavLink key={l.key} link={l} />
              ))}
            </nav>

            {/* mobile logo */}
            <a
              href="/"
              data-testid="logo-link-mobile"
              onClick={(e) => {
                e.preventDefault();
                goTo("/");
                close();
              }}
              className="xl:hidden flex items-center gap-2"
            >
              <div className="w-12 h-12 rounded-full bg-white flex flex-col items-center justify-center shadow-soft">
                <svg viewBox="0 0 40 26" className="w-7" aria-hidden="true">
                  <path d="M6 22a14 14 0 0 1 28 0" stroke="#F9A8D4" strokeWidth="5" strokeLinecap="round" fill="none" />
                  <path d="M13 22a7 7 0 0 1 14 0" stroke="#8CE0C5" strokeWidth="5" strokeLinecap="round" fill="none" />
                </svg>
              </div>
              <span className="font-display font-semibold text-forest leading-tight text-sm">
                Neni’s Playhouse
                <span className="block text-[10px] tracking-[0.2em] text-[#9B6BD1] font-bold">CHILD CARE</span>
              </span>
            </a>

            {/* right side */}
            <div className="flex items-center gap-4 sm:gap-5 flex-[1.45] justify-end">
              <nav className="hidden xl:flex items-center gap-4 whitespace-nowrap pl-[calc(22.3%+136px)]">
                {LINKS_R.map((l) => (
                  <NavLink key={l.key} link={l} />
                ))}
              </nav>
              <LangToggle className="shrink-0" />
              <EnrollButton className="hidden 2xl:inline-flex" />
              <button
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
                onClick={() => setOpen((v) => !v)}
                className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-forest shadow-soft"
              >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>

            {/* center notch with hanging logo (desktop) */}
            <div className="hidden xl:block absolute left-1/2 -translate-x-1/2 top-0 pointer-events-none">
              <div className="relative h-[136px] w-[240px] bg-sage rounded-b-[120px] flex items-end justify-center pb-1">
                <div
                  className="absolute -left-6 top-16 w-6 h-6"
                  style={{ background: "radial-gradient(circle at 0% 0%, transparent 23.8px, #D9EEFC 24px)" }}
                />
                <div
                  className="absolute -right-6 top-16 w-6 h-6"
                  style={{ background: "radial-gradient(circle at 100% 0%, transparent 23.8px, #D9EEFC 24px)" }}
                />
                <a
                  href="/"
                  data-testid="logo-link"
                  onClick={(e) => {
                    e.preventDefault();
                    goTo("/");
                  }}
                  className="pointer-events-auto -mb-0"
                >
                  <LogoSimple size={122} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* mobile drawer */}
        {open && (
          <div data-testid="mobile-menu" className="xl:hidden mt-2 rounded-3xl bg-sage shadow-card p-5 flex flex-col gap-4">
            {[...LINKS_L, ...LINKS_R].map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={(e) => {
                  e.preventDefault();
                  goTo(l.href);
                  close();
                }}
                data-testid={`mobile-nav-link-${l.tid}`}
                className="font-display font-medium text-forest text-base py-1.5 border-b border-forest/10 last:border-0"
              >
                {s.nav[l.key]}
              </a>
            ))}
            <a
              data-testid="mobile-call-button"
              href="tel:+19174960201"
              onClick={close}
              className="flex items-center gap-2 font-display font-medium text-forest"
            >
              <Phone className="w-4 h-4" /> {s.nav.callText}
            </a>
            <div className="flex items-center gap-3">
              <EnrollButton onClick={close} />
              <LangToggle />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
